import { useState, type ReactNode } from "react";
import {
  Box, Button, Card, CardActionArea, CardContent, MenuItem, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography, useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router";
import ReactECharts from "echarts-for-react";
import { api } from "../../api/client";
import { useAuth } from "../../api/auth-context";
import { formatNumber } from "@safrater/ui";

const brl = (n: number) => n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
const num = (n: number) => n.toLocaleString("pt-BR");
const dt = (s: string) => new Date(s).toLocaleDateString("pt-BR");
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

const STATUS_LABEL: Record<string, string> = { active: "Ativa", expiring: "A vencer", expired: "Expirada" };

interface Counters { users: number; institutions: number; approvals: number; activeLicenses: number; paymentsAwaiting: number }
interface Opcao { id: string; name?: string; key?: string }

interface Executive {
  period: { from: string; to: string; prevFrom: string; prevTo: string };
  kpis: {
    activeInstitutions: number; activeUsers: number; activeLicenses: number; licensesExpiring: number;
    mrrTotal: number; receivable: { total: number; institutionsInDebt: number };
  };
  revenueTrend: { current: number; previous: number; deltaPct: number | null };
  byProject: { key: string; name: string; billed: number; mrr: number; activeLicenses: number; totalLicenses: number }[];
  byInstitution: { institutionId: string; name: string; cnpj: string; billed: number; paid: number; balance: number }[];
  adoption: { key: string; name: string; activeLicenses: number; totalLicenses: number }[];
  mrr: { total: number; byModule: { key: string; name: string; value: number }[] };
  licensesByStatus: { status: string; count: number }[];
  expiring: { licenseId: string; institution: string; module: string; validUntil: string; daysLeft: number }[];
  topInstitutionsByUsers: { institutionId: string; name: string; users: number }[];
}

async function fetchCounters(isMaster: boolean, canManage: boolean): Promise<Counters> {
  // Usuário comum não acessa as listas de gestão (backend retorna 403) — só
  // consultamos os contadores quando o papel permite, com catch defensivo.
  const zero = Promise.resolve({ data: { total: 0 } });
  const [users, institutions, approvals, licenses, payments] = await Promise.all([
    canManage ? api.get<{ total: number }>("/users", { params: { page: 1, page_size: 1 } }).catch(() => ({ data: { total: 0 } })) : zero,
    canManage ? api.get<{ total: number }>("/institutions", { params: { page: 1, page_size: 1 } }).catch(() => ({ data: { total: 0 } })) : zero,
    isMaster ? api.get<unknown[]>("/institutions/approvals/pending").catch(() => ({ data: [] as unknown[] })) : Promise.resolve({ data: [] as unknown[] }),
    canManage ? api.get<{ total: number }>("/licenses", { params: { page: 1, page_size: 1, status: "active" } }).catch(() => ({ data: { total: 0 } })) : zero,
    isMaster ? api.get<{ total: number }>("/payments", { params: { page: 1, page_size: 1, status: "awaiting_review" } }).catch(() => ({ data: { total: 0 } })) : zero,
  ]);
  return {
    users: (users.data as { total: number }).total,
    institutions: (institutions.data as { total: number }).total,
    approvals: Array.isArray(approvals.data) ? approvals.data.length : 0,
    activeLicenses: (licenses.data as { total: number }).total,
    paymentsAwaiting: (payments.data as { total: number }).total,
  };
}

export function DashboardPage() {
  const { user } = useAuth();
  const theme = useTheme();
  const navigate = useNavigate();
  const isMaster = !!user?.isMasterAdmin;
  const canManage = isMaster || (user?.adminInstitutionIds?.length ?? 0) > 0;

  // Filtros (default: último ano até hoje). "Atualizar" aplica os valores.
  const hoje = new Date();
  const [inicio, setInicio] = useState(isoDay(new Date(hoje.getFullYear() - 1, hoje.getMonth(), 1)));
  const [fim, setFim] = useState(isoDay(hoje));
  const [moduleId, setModuleId] = useState("");
  const [institutionId, setInstitutionId] = useState("");
  const [filtro, setFiltro] = useState({ from: inicio, to: fim, moduleId: "", institutionId: "" });

  const { data: counts } = useQuery({ queryKey: ["dashboard-counts", isMaster, canManage], queryFn: () => fetchCounters(isMaster, canManage) });
  const { data: modules } = useQuery({ queryKey: ["dashboard-modules"], queryFn: async () => (await api.get<Opcao[]>("/modules")).data, enabled: isMaster });
  const { data: institutions } = useQuery({ queryKey: ["dashboard-institutions"], queryFn: async () => (await api.get<{ data: Opcao[] }>("/institutions", { params: { page: 1, page_size: 200 } })).data.data, enabled: isMaster });
  const { data: exec, isFetching } = useQuery({
    queryKey: ["dashboard-executive", filtro],
    queryFn: async () => (await api.get<Executive>("/financial/executive", { params: { from: filtro.from || undefined, to: filtro.to || undefined, moduleId: filtro.moduleId || undefined, institutionId: filtro.institutionId || undefined } })).data,
    enabled: isMaster,
    placeholderData: (prev) => prev,
  });

  const aplicar = () => setFiltro({ from: inicio, to: fim, moduleId, institutionId });

  const textColor = theme.palette.text.secondary;
  const palette = [theme.palette.primary.main, theme.palette.secondary.main, theme.palette.success.main, theme.palette.warning.main, theme.palette.info.main, theme.palette.error.main, "#8e75c9", "#5aa9a3"];
  const chartBase = { backgroundColor: "transparent", textStyle: { color: textColor, fontFamily: theme.typography.fontFamily }, color: palette };

  const licOption = exec && {
    ...chartBase,
    tooltip: { trigger: "item", formatter: (p: { name: string; value: number; percent: number }) => `${p.name}<br/><b>${p.value}</b> (${p.percent}%)` },
    legend: { bottom: 0, textStyle: { color: textColor } },
    series: [{
      type: "pie", radius: ["40%", "68%"],
      itemStyle: { borderColor: theme.palette.background.paper, borderWidth: 2 },
      label: { color: textColor, formatter: "{b}: {c}" },
      data: exec.licensesByStatus.map((s) => ({ name: STATUS_LABEL[s.status] ?? s.status, value: s.count })),
    }],
  };

  const delta = exec?.revenueTrend.deltaPct ?? null;
  const receivable = exec?.kpis.receivable.total ?? 0;
  const expiring = exec?.kpis.licensesExpiring ?? 0;

  // KPI cards (master admin usa o executivo; admin de instituição usa contadores).
  const kpis: { label: string; value?: string; sub?: string; color?: string; to?: string; show: boolean }[] = isMaster
    ? [
        { label: "Instituições ativas", value: exec ? num(exec.kpis.activeInstitutions) : undefined, to: "/institutions", show: true },
        { label: "Usuários ativos", value: exec ? num(exec.kpis.activeUsers) : undefined, to: "/users", show: true },
        { label: "Licenças ativas", value: exec ? num(exec.kpis.activeLicenses) : undefined, to: "/licenses", show: true },
        { label: "Receita recorrente (MRR)", value: exec ? brl(exec.kpis.mrrTotal) : undefined, to: "/licenses", show: true },
        { label: "Faturamento no período", value: exec ? brl(exec.revenueTrend.current) : undefined, sub: delta === null ? "sem base anterior" : `${delta >= 0 ? "▲" : "▼"} ${Math.abs(delta)}% vs. período anterior`, color: delta === null ? undefined : delta >= 0 ? theme.palette.success.main : theme.palette.error.main, to: "/financial", show: true },
        { label: "Total a receber", value: exec ? brl(receivable) : undefined, sub: exec ? `${exec.kpis.receivable.institutionsInDebt} instituição(ões) em débito` : undefined, color: receivable > 0 ? theme.palette.error.main : theme.palette.success.main, to: "/financial", show: true },
        { label: "Licenças a vencer (30d)", value: exec ? num(expiring) : undefined, color: expiring > 0 ? theme.palette.warning.main : theme.palette.success.main, to: "/licenses", show: true },
        { label: "Aprovações pendentes", value: counts ? num(counts.approvals) : undefined, color: counts?.approvals ? theme.palette.warning.main : undefined, to: "/approvals", show: true },
        { label: "Pagamentos aguardando", value: counts ? num(counts.paymentsAwaiting) : undefined, color: counts?.paymentsAwaiting ? theme.palette.warning.main : undefined, to: "/payments", show: true },
      ]
    : [
        { label: "Instituições", value: counts ? formatNumber(counts.institutions) : undefined, to: "/institutions", show: canManage },
        { label: "Usuários", value: counts ? formatNumber(counts.users) : undefined, to: "/users", show: canManage },
        { label: "Licenças ativas", value: counts ? formatNumber(counts.activeLicenses) : undefined, to: "/licenses", show: canManage },
      ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>Painel executivo</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Visão consolidada da plataforma — clique nos indicadores para ir à tela relacionada.
      </Typography>

      {/* Barra de filtros — período, projeto e instituição (master admin) */}
      {isMaster && (
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          {/* Grid responsivo: datas ≤ lg-2; Projeto/Instituição no MÁXIMO lg-6 / xl-4. */}
          <Grid container spacing={1.5} alignItems="center">
            <Grid size={{ xs: 6, sm: 3, md: 2, lg: 2, xl: 2 }}>
              <TextField fullWidth label="Início" type="date" size="small" value={inicio} onChange={(e) => setInicio(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 6, sm: 3, md: 2, lg: 2, xl: 2 }}>
              <TextField fullWidth label="Fim" type="date" size="small" value={fim} onChange={(e) => setFim(e.target.value)} InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3, xl: 3 }}>
              <TextField fullWidth select size="small" label="Projeto" value={moduleId} onChange={(e) => setModuleId(e.target.value)}>
                <MenuItem value="">Todos os projetos</MenuItem>
                {modules?.map((m) => <MenuItem key={m.id} value={m.id}>{m.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 4, xl: 4 }}>
              <TextField fullWidth select size="small" label="Instituição" value={institutionId} onChange={(e) => setInstitutionId(e.target.value)}>
                <MenuItem value="">Todas as instituições</MenuItem>
                {institutions?.map((i) => <MenuItem key={i.id} value={i.id}>{i.name}</MenuItem>)}
              </TextField>
            </Grid>
            <Grid size="auto">
              <Button variant="contained" onClick={aplicar} disabled={isFetching}>Atualizar</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* KPI cards */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 3 }}>
        {kpis.filter((k) => k.show).map((k) => (
          <KpiCard key={k.label} k={k} navigate={navigate} />
        ))}
      </Box>

      {/* Painéis executivos (master admin) */}
      {isMaster && exec && (
        <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
          <Panel title="Somatório por projeto" subtitle="Faturamento no período, MRR e licenças ativas" to="/licenses" navigate={navigate}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Projeto</TableCell>
                  <TableCell align="right">Faturamento</TableCell>
                  <TableCell align="right">MRR</TableCell>
                  <TableCell align="right">Ativas</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exec.byProject.map((p) => (
                  <TableRow key={p.key}>
                    <TableCell>{p.name}</TableCell>
                    <TableCell align="right">{brl(p.billed)}</TableCell>
                    <TableCell align="right">{brl(p.mrr)}</TableCell>
                    <TableCell align="right">{num(p.activeLicenses)}</TableCell>
                  </TableRow>
                ))}
                {!exec.byProject.length && <Vazio cols={4} />}
              </TableBody>
            </Table>
          </Panel>

          <Panel title="Somatório por instituição" subtitle="Faturado, pago e saldo no período (top 12)" to="/financial" navigate={navigate}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Instituição</TableCell>
                  <TableCell align="right">Faturado</TableCell>
                  <TableCell align="right">Pago</TableCell>
                  <TableCell align="right">Saldo</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exec.byInstitution.map((i) => (
                  <TableRow key={i.institutionId}>
                    <TableCell>{i.name}</TableCell>
                    <TableCell align="right">{brl(i.billed)}</TableCell>
                    <TableCell align="right">{brl(i.paid)}</TableCell>
                    <TableCell align="right" sx={{ color: i.balance > 0 ? "error.main" : "success.main" }}>{brl(i.balance)}</TableCell>
                  </TableRow>
                ))}
                {!exec.byInstitution.length && <Vazio cols={4} />}
              </TableBody>
            </Table>
          </Panel>

          <Panel title="Adesão por sistema" subtitle="Instituições com licença ativa por módulo" to="/modules" navigate={navigate}>
            <BarList itens={exec.adoption.map((a) => ({ chave: a.name, total: a.activeLicenses }))} />
          </Panel>

          <Panel title="MRR por projeto" subtitle="Receita recorrente mensal das licenças ativas" to="/licenses" navigate={navigate}>
            <BarList itens={exec.mrr.byModule.map((m) => ({ chave: m.name, total: m.value }))} format={brl} />
          </Panel>

          <Panel title="Licenças por status" subtitle="Distribuição das licenças no escopo filtrado" to="/licenses" navigate={navigate}>
            <ReactECharts option={licOption} style={{ height: 260 }} notMerge lazyUpdate />
          </Panel>

          <Panel title="Licenças a vencer (30 dias)" subtitle="Trials e mensais próximos do vencimento" to="/licenses" navigate={navigate}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Instituição</TableCell>
                  <TableCell>Projeto</TableCell>
                  <TableCell align="right">Vence em</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {exec.expiring.map((e) => (
                  <TableRow key={e.licenseId}>
                    <TableCell>{e.institution}</TableCell>
                    <TableCell>{e.module}</TableCell>
                    <TableCell align="right" sx={{ color: e.daysLeft <= 7 ? "error.main" : "warning.main", whiteSpace: "nowrap" }}>
                      {dt(e.validUntil)} ({e.daysLeft}d)
                    </TableCell>
                  </TableRow>
                ))}
                {!exec.expiring.length && <Vazio cols={3} texto="Nenhuma licença a vencer." />}
              </TableBody>
            </Table>
          </Panel>

          <Panel title="Top instituições por usuários" subtitle="Vínculos ativos por instituição" to="/institutions" navigate={navigate}>
            <BarList itens={exec.topInstitutionsByUsers.map((t) => ({ chave: t.name, total: t.users }))} />
          </Panel>
        </Stack>
      )}
    </Box>
  );
}

function KpiCard({ k, navigate }: { k: { label: string; value?: string; sub?: string; color?: string; to?: string }; navigate: (to: string) => void }) {
  const body = (
    <CardContent>
      <Typography variant="overline" color="text.secondary">{k.label}</Typography>
      <Typography variant="h5" sx={{ color: k.color ?? "text.primary", fontWeight: 700 }}>{k.value ?? "…"}</Typography>
      {k.sub && <Typography variant="caption" color="text.secondary" display="block">{k.sub}</Typography>}
    </CardContent>
  );
  return (
    <Card variant="outlined" sx={{ flex: "1 1 200px", minWidth: 190 }}>
      {k.to ? <CardActionArea onClick={() => navigate(k.to!)} sx={{ height: "100%" }}>{body}</CardActionArea> : body}
    </Card>
  );
}

function Panel({ title, subtitle, to, navigate, children }: { title: string; subtitle: string; to: string; navigate: (to: string) => void; children: ReactNode }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: "1 1 380px", minWidth: 320 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" sx={{ mb: 1.5 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="caption" color="text.secondary">{subtitle}</Typography>
        </Box>
        <Typography variant="caption" sx={{ color: "primary.main", cursor: "pointer", whiteSpace: "nowrap" }} onClick={() => navigate(to)}>ver detalhes →</Typography>
      </Stack>
      {children}
    </Paper>
  );
}

function BarList({ itens, format = num }: { itens: { chave: string; total: number }[]; format?: (n: number) => string }) {
  if (!itens.length) return <Typography variant="body2" color="text.secondary">Sem registros no período.</Typography>;
  const max = Math.max(...itens.map((i) => i.total), 1);
  return (
    <Stack spacing={1}>
      {itens.map((i) => (
        <Box key={i.chave}>
          <Stack direction="row" justifyContent="space-between">
            <Typography variant="body2">{i.chave}</Typography>
            <Typography variant="body2"><b>{format(i.total)}</b></Typography>
          </Stack>
          <Box sx={{ height: 6, borderRadius: 3, bgcolor: "action.hover", mt: 0.25 }}>
            <Box sx={{ height: 6, borderRadius: 3, width: `${(i.total / max) * 100}%`, bgcolor: "primary.main" }} />
          </Box>
        </Box>
      ))}
    </Stack>
  );
}

function Vazio({ cols, texto = "Sem registros no período." }: { cols: number; texto?: string }) {
  return <TableRow><TableCell colSpan={cols}><Typography variant="body2" color="text.secondary">{texto}</Typography></TableCell></TableRow>;
}
